import { useSuggestedAddresses } from '@monofront/business/hooks';
import {
  IAddressV2,
  IGetAddressesWithDefaultOneQueryResult,
  ISuggestedAddress,
} from '@monofront/types';
import Button from '@monofront/design-system/components/Button';
import AutoComplete, {
  IAutoComplete,
} from '@monofront/design-system/components/Form/AutoComplete';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import {
  ErrorText,
  Input,
  Label,
  TextInput,
} from '@monofront/design-system/components/FormV2';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { RichText } from 'prismic-reactjs';
import { useQuery } from '@apollo/client';
import { GET_ADDRESSES_WITH_DEFAULT_ONE } from '@monofront/business/queries';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { IPrismicProductEligibilityModal } from './types';
import { useEligibility, useFeatureIsOn } from '@monofront/business/contexts';
import { FEATURE_FLAG_MAPBOX_AUTO_COMPLETE } from '@monofront/business/constants';
import getConfig from 'next/config';
import useI18n from '@hooks/i18n/useI18n';
import { v4 as uuid } from 'uuid';
import getThemeColor from '@monofront/design-system/utils/getThemeColor';

const AddressAutoComplete = AutoComplete as IAutoComplete<{
  street: string;
  postcode: string;
  city: string;
  countryCode?: string;
}>;

interface IFreshAddressFormProps {
  onSuggestedAddressSelect: (address: ISuggestedAddress | IAddressV2) => void;
  document?: IPrismicProductEligibilityModal;
}

type FreshAddressFormValues = {
  street: string;
  postcode: string;
  city: string;
};

const {
  publicRuntimeConfig: { mapboxToken },
} = getConfig();
const sessionToken = uuid();

const FreshAddressForm: React.FC<IFreshAddressFormProps> = ({
  onSuggestedAddressSelect,
  document,
}) => {
  const tFresh = useTranslations('Fresh');
  const tAccount = useTranslations('Account');
  const { eligibility } = useEligibility();
  const { locale, availableCountryLocale, isGermanMarket } = useI18n();
  const isMapboxAutoCompleteEnabled = useFeatureIsOn(
    FEATURE_FLAG_MAPBOX_AUTO_COMPLETE,
  );
  const usePlainInput = isGermanMarket && !isMapboxAutoCompleteEnabled;
  const isUserEligible = Object.values(eligibility).some((value) => value);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setError,
    setValue,
  } = useForm<FreshAddressFormValues>({
    mode: 'onChange',
  });

  const { data } = useQuery<IGetAddressesWithDefaultOneQueryResult>(
    GET_ADDRESSES_WITH_DEFAULT_ONE,
    {
      ssr: false,
      fetchPolicy: 'cache-and-network',
    },
  );
  const addresses = data?.getAddressesWithDefaultOne;

  const {
    writtenStreet,
    setWrittenStreet,
    suggestedAddresses,
    isSuggestedAddressLoading,
  } = useSuggestedAddresses({
    mapboxToken,
    locale,
    sessionToken,
    searchableCountry: availableCountryLocale,
  });

  const onSubmit = (data: FreshAddressFormValues) => {
    if (usePlainInput) {
      // German plain-text path: no autocomplete suggestion to select, submit the typed values
      onSuggestedAddressSelect({
        street: data.street,
        postcode: data.postcode,
        city: data.city,
        label: data.street,
        countryCode: 'DE',
        type: 'address',
      });

      return;
    }

    // display an error on press enter as no address has been selected
    setError('street', {
      type: 'addressNotSelected',
      message: tFresh('errorAddressNotSelected'),
    });
  };

  const onSelect = (address: ISuggestedAddress | IAddressV2) => {
    const { street, postcode, city } = address;

    setValue('street', `${street}, ${postcode}, ${city}`);

    setTimeout(() => {
      // Let the user see the address he has selected
      onSuggestedAddressSelect(address);
    }, 600);
  };

  if (!document) return <HeartLoader />;

  return (
    <>
      <InformativeBox
        backgroundColor={getThemeColor(
          isUserEligible
            ? document.eligible_user_informative_box_background_color
            : document.informative_box_background_color,
        )}
        img={
          isUserEligible
            ? document.eligible_user_informative_box_picto
            : document.informative_box_picto
        }
        hasImageOnMobile
      >
        <RichText
          render={
            isUserEligible
              ? document.eligible_user_informative_box_text
              : document.informative_box_text
          }
        />
      </InformativeBox>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        {usePlainInput ? (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <TextInput id="street" isInvalid={Boolean(errors.street)}>
              <Label>{tFresh('addressInputLabel')}</Label>
              <Input
                autoComplete="off"
                type="text"
                placeholder={tAccount('suggestedAddressPlaceholder')}
                {...register('street', {
                  required: {
                    value: true,
                    message: tAccount('suggestedAddressRequiredMessage'),
                  },
                })}
                dataTestId="AddressForm-Input-street"
              />
              {errors.street && (
                <ErrorText dataTestId="FreshAddressForm-Input-error">
                  {errors.street.message as string}
                </ErrorText>
              )}
            </TextInput>
            <TextInput id="postcode" isInvalid={Boolean(errors.postcode)}>
              <Label>{tAccount('zipCode')}</Label>
              <Input
                autoComplete="off"
                inputMode="numeric"
                type="text"
                placeholder={tAccount('zipCodePlaceholder')}
                {...register('postcode', {
                  required: {
                    value: true,
                    message: tAccount('zipCodeRequiredMessage'),
                  },
                })}
                dataTestId="AddressForm-Input-postcode"
              />
              {errors.postcode && (
                <ErrorText>{errors.postcode.message as string}</ErrorText>
              )}
            </TextInput>
            <TextInput id="city" isInvalid={Boolean(errors.city)}>
              <Label>{tAccount('city')}</Label>
              <Input
                autoComplete="off"
                type="text"
                placeholder={tAccount('cityPlaceholder')}
                {...register('city', {
                  required: {
                    value: true,
                    message: tAccount('cityRequiredMessage'),
                  },
                })}
                dataTestId="AddressForm-Input-city"
              />
              {errors.city && (
                <ErrorText>{errors.city.message as string}</ErrorText>
              )}
            </TextInput>
            <Button type="submit">{tAccount('addressCreateSubmit')}</Button>
          </div>
        ) : (
          <TextInput id="street" isInvalid={Boolean(errors.street)}>
            <Label>{tFresh('addressInputLabel')}</Label>
            <AddressAutoComplete
              isLoading={isSuggestedAddressLoading}
              options={
                writtenStreet?.length
                  ? suggestedAddresses?.map(
                      ({
                        label,
                        street,
                        postcode,
                        city,
                        countryCode,
                        type,
                      }) => ({
                        id: label,
                        label,
                        value: {
                          street,
                          postcode,
                          city,
                          countryCode,
                          type,
                        },
                      }),
                    )
                  : undefined
              }
              handleSelect={onSelect}
              renderInput={(params) => (
                <Input
                  {...params}
                  autoComplete="off"
                  type="text"
                  placeholder={tAccount('suggestedAddressPlaceholder')}
                  {...register('street', {
                    required: {
                      value: true,
                      message: tAccount('suggestedAddressRequiredMessage'),
                    },
                  })}
                  onChange={(e) => setWrittenStreet(e.target.value)}
                  dataTestId="AddressForm-Input-street"
                />
              )}
            />
            {errors.street && (
              <ErrorText dataTestId="FreshAddressForm-Input-error">
                {errors.street.message as string}
              </ErrorText>
            )}
          </TextInput>
        )}
      </form>

      {addresses && addresses.length > 0 && (
        <div className="saved-addresses">
          <p className="hxm">{tFresh('savedAddresses')}</p>
          {addresses?.map((address) => (
            <Button
              key={address.id!}
              variant="transparent"
              size="small"
              hasMinWidth={false}
              block={false}
              onClick={() => onSelect(address)}
            >
              {tFresh('completeAddress', {
                street: address.street,
                postcode: address.postcode,
                city: address.city,
              })}
            </Button>
          ))}
        </div>
      )}

      <style jsx>{`
        .form {
          margin-top: var(--space-4);
        }
        .saved-addresses {
          margin-top: var(--space-5);
        }
        .saved-addresses :global(button) {
          margin-top: var(--space-4);
          font-weight: var(--font-weight-light);
        }
      `}</style>
    </>
  );
};

export default FreshAddressForm;
